import { useQueryNormalizer } from '@normy/react-query';
import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import type {
  SetExperienceVisibilityMutation,
  SetExperienceVisibilityMutationVariables,
} from './useSetExperienceVisibility.generated';

// Publishing is owner-only server-side, which is why this isn't a field on
// updateExperience. Selecting just `{ id isPublic }` is enough: the normalizer keys
// on `id`, so this patches every cached query holding the experience without
// re-fetching the whole PageExperience fragment.
const query = gql`
  mutation SetExperienceVisibility($input: SetExperienceVisibilityInput!) {
    setExperienceVisibility(input: $input) {
      id
      isPublic
    }
  }
`;

export const useSetExperienceVisibility = () => {
  const queryNormalizer = useQueryNormalizer();
  // The flag lives on the planner root, so resolve through the effective
  // experience — the displayed state and the mutation target can't disagree.
  const experience = useEffectiveExperience();
  const experienceId = String(experience.id);

  const { mutate, error, isPending } = useMutation<
    SetExperienceVisibilityMutation,
    QueryError,
    SetExperienceVisibilityMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        SetExperienceVisibilityMutation,
        SetExperienceVisibilityMutationVariables
      >(query, variables);
    },
    // Flip the switch immediately and roll back if the server refuses. No
    // onSuccess work: visibility doesn't affect the parent's children[] or the
    // event collection, and the response reconciles the cache on its own.
    onMutate: ({ input }) => ({
      rollbackData: queryNormalizer.getObjectById<PageExperience>(
        input.experienceId
      ),
      optimisticData: {
        id: input.experienceId,
        isPublic: input.isPublic,
      },
    }),
  });

  const setVisibility = (
    isPublic: boolean,
    options?: MutateOptions<
      SetExperienceVisibilityMutation,
      QueryError,
      SetExperienceVisibilityMutationVariables
    >
  ) => mutate({ input: { experienceId, isPublic } }, options);

  return {
    setVisibility,
    error,
    isPending,
  };
};
